// System Design Topics Data with comprehensive content
import React from 'react';

export interface SystemDesignTopicData {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedReadTime: string;
  rating: number;
  learners: string;
  sections: {
    id: string;
    label: string;
  }[];
  relatedTopics: {
    path: string;
    title: string;
  }[];
  content: {
    introduction: React.ReactNode;
    sectionContent: {
      [sectionId: string]: React.ReactNode;
    };
  };
  nextTopic?: {
    path: string;
    title: string;
  };
}

export const systemDesignTopicsData: Record<string, SystemDesignTopicData> = {
  scalability: {
    id: 'scalability',
    title: 'Scalability Fundamentals',
    description: 'Learn how to design systems that can handle growing load and traffic',
    difficulty: 'intermediate',
    estimatedReadTime: '8 min read',
    rating: 4.8,
    learners: '24.5k learners',
    sections: [
      { id: 'horizontal-vs-vertical', label: '1. Horizontal vs Vertical Scaling' },
      { id: 'load-distribution', label: '2. Load Distribution Strategies' },
      { id: 'database-scaling', label: '3. Database Scaling Techniques' },
      { id: 'caching-strategies', label: '4. Caching for Scale' },
      { id: 'real-world-examples', label: '5. Real-world Examples' }
    ],
    relatedTopics: [
      { path: '/system-design/load-balancing', title: 'Load Balancing' },
      { path: '/system-design/databases', title: 'Database Design' },
      { path: '/system-design/caching', title: 'Caching Strategies' }
    ],
    content: {
      introduction: null, // Will be filled by the component
      sectionContent: {} // Will be filled by the component
    },
    nextTopic: {
      path: '/system-design/load-balancing',
      title: 'Load Balancing'
    }
  },
  
  'load-balancing': {
    id: 'load-balancing',
    title: 'Load Balancing',
    description: 'Distribute traffic across multiple servers efficiently for high availability',
    difficulty: 'intermediate',
    estimatedReadTime: '7 min read',
    rating: 4.7,
    learners: '22.8k learners',
    sections: [
      { id: 'fundamentals', label: '1. Load Balancing Fundamentals' },
      { id: 'algorithms', label: '2. Load Balancing Algorithms' },
      { id: 'types', label: '3. Types of Load Balancers' },
      { id: 'health-checks', label: '4. Health Checks & Failover' },
      { id: 'implementation', label: '5. Implementation Examples' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/system-design/microservices', title: 'Microservices' },
      { path: '/system-design/security', title: 'System Security' }
    ],
    content: {
      introduction: null,
      sectionContent: {}
    },
    nextTopic: {
      path: '/system-design/databases',
      title: 'Database Design'
    }
  },

  databases: {
    id: 'databases',
    title: 'Database Design',
    description: 'SQL vs NoSQL, sharding, and replication strategies for scalable data storage',
    difficulty: 'advanced',
    estimatedReadTime: '12 min read',
    rating: 4.7,
    learners: '19.8k learners',
    sections: [
      { id: 'fundamentals', label: '1. Data Model' },
      { id: 'schema', label: '2. Schema' },
      { id: 'scalability', label: '3. Scalability' },
      { id: 'query-language', label: '4. Query Language' },
      { id: 'transactions', label: '5. Transaction Support' },
      { id: 'performance', label: '6. Performance' },
      { id: 'use-cases', label: '7. Use Cases' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/topics/nodejs', title: 'Node.js Development' },
      { path: '/system-design/caching', title: 'Caching Strategies' }
    ],
    content: {
      introduction: null,
      sectionContent: {}
    },
    nextTopic: {
      path: '/system-design/caching',
      title: 'Caching Strategies'
    }
  },

  caching: {
    id: 'caching',
    title: 'Caching Strategies',
    description: 'Redis, Memcached, and CDN implementation for improved performance',
    difficulty: 'intermediate',
    estimatedReadTime: '9 min read',
    rating: 4.6,
    learners: '21.2k learners',
    sections: [
      { id: 'fundamentals', label: '1. Caching Fundamentals' },
      { id: 'strategies', label: '2. Caching Strategies' },
      { id: 'technologies', label: '3. Caching Technologies' },
      { id: 'patterns', label: '4. Common Patterns' },
      { id: 'best-practices', label: '5. Best Practices' }
    ],
    relatedTopics: [
      { path: '/system-design/databases', title: 'Database Design' },
      { path: '/system-design/performance', title: 'Performance Optimization' },
      { path: '/system-design/scalability', title: 'Scalability' }
    ],
    content: {
      introduction: null,
      sectionContent: {}
    },
    nextTopic: {
      path: '/system-design/security',
      title: 'System Security'
    }
  },

  security: {
    id: 'security',
    title: 'System Security',
    description: 'Authentication, authorization, and data protection in distributed systems',
    difficulty: 'advanced',
    estimatedReadTime: '10 min read',
    rating: 4.9,
    learners: '18.7k learners',
    sections: [
      { id: 'fundamentals', label: '1. Security Fundamentals' },
      { id: 'authentication', label: '2. Authentication' },
      { id: 'authorization', label: '3. Authorization' },
      { id: 'data-protection', label: '4. Data Protection' },
      { id: 'common-attacks', label: '5. Common Attacks & Prevention' }
    ],
    relatedTopics: [
      { path: '/system-design/microservices', title: 'Microservices' },
      { path: '/topics/nodejs', title: 'Node.js Development' },
      { path: '/system-design/load-balancing', title: 'Load Balancing' }
    ],
    content: {
      introduction: null,
      sectionContent: {}
    },
    nextTopic: {
      path: '/system-design/microservices',
      title: 'Microservices Architecture'
    }
  },

  microservices: {
    id: 'microservices',
    title: 'Microservices Architecture',
    description: 'Service-oriented architecture and communication patterns for scalable systems',
    difficulty: 'advanced',
    estimatedReadTime: '11 min read',
    rating: 4.8,
    learners: '16.9k learners',
    sections: [
      { id: 'fundamentals', label: '1. Microservices Fundamentals' },
      { id: 'design-principles', label: '2. Design Principles' },
      { id: 'communication', label: '3. Service Communication' },
      { id: 'data-management', label: '4. Data Management' },
      { id: 'deployment', label: '5. Deployment & Monitoring' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/system-design/load-balancing', title: 'Load Balancing' },
      { path: '/topics/docker', title: 'Docker & Containerization' }
    ],
    content: {
      introduction: null,
      sectionContent: {}
    }
  }
};

// Case Studies Data
export interface CaseStudyData {
  id: string;
  title: string;
  description: string;
  complexity: 'High' | 'Very High';
  estimatedTime: string;
  features: string[];
  difficulty: 'advanced' | 'expert';
  estimatedReadTime: string;
  rating: number;
  learners: string;
  sections: {
    id: string;
    label: string;
  }[];
  relatedTopics: {
    path: string;
    title: string;
  }[];
}

export const caseStudiesData: Record<string, CaseStudyData> = {
  whatsapp: {
    id: 'whatsapp',
    title: 'Design WhatsApp',
    description: 'Real-time messaging system with billions of users and end-to-end encryption',
    complexity: 'Very High',
    estimatedTime: '45-60 min',
    features: ['Real-time messaging', 'Group chats', 'File sharing', 'Status updates', 'End-to-end encryption'],
    difficulty: 'expert',
    estimatedReadTime: '15 min read',
    rating: 4.9,
    learners: '32.1k learners',
    sections: [
      { id: 'requirements', label: '1. Requirements Analysis' },
      { id: 'capacity-estimation', label: '2. Capacity Estimation' },
      { id: 'high-level-design', label: '3. High-Level Design' },
      { id: 'detailed-design', label: '4. Detailed Design' },
      { id: 'scaling', label: '5. Scaling & Optimization' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/system-design/databases', title: 'Database Design' },
      { path: '/system-design/security', title: 'System Security' }
    ]
  },

  twitter: {
    id: 'twitter',
    title: 'Design Twitter',
    description: 'Social media platform with timeline generation and trending features',
    complexity: 'High',
    estimatedTime: '45-60 min',
    features: ['Tweet timeline', 'Following system', 'Trending topics', 'Notifications', 'Search'],
    difficulty: 'advanced',
    estimatedReadTime: '14 min read',
    rating: 4.8,
    learners: '28.7k learners',
    sections: [
      { id: 'requirements', label: '1. Requirements Analysis' },
      { id: 'capacity-estimation', label: '2. Capacity Estimation' },
      { id: 'high-level-design', label: '3. High-Level Design' },
      { id: 'database-design', label: '4. Database Design' },
      { id: 'timeline-generation', label: '5. Timeline Generation' },
      { id: 'scaling', label: '6. Scaling & Optimization' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/system-design/caching', title: 'Caching Strategies' },
      { path: '/system-design/load-balancing', title: 'Load Balancing' }
    ]
  },

  uber: {
    id: 'uber',
    title: 'Design Uber',
    description: 'Ride-sharing application with real-time location tracking and matching algorithms',
    complexity: 'Very High',
    estimatedTime: '60+ min',
    features: ['Real-time location', 'Matching algorithm', 'Payment system', 'Rating system', 'Trip tracking'],
    difficulty: 'expert',
    estimatedReadTime: '16 min read',
    rating: 4.9,
    learners: '25.3k learners',
    sections: [
      { id: 'requirements', label: '1. Requirements Analysis' },
      { id: 'capacity-estimation', label: '2. Capacity Estimation' },
      { id: 'high-level-design', label: '3. High-Level Design' },
      { id: 'location-service', label: '4. Location Service' },
      { id: 'matching-algorithm', label: '5. Matching Algorithm' },
      { id: 'payment-system', label: '6. Payment System' },
      { id: 'scaling', label: '7. Scaling & Optimization' }
    ],
    relatedTopics: [
      { path: '/system-design/scalability', title: 'Scalability' },
      { path: '/system-design/databases', title: 'Database Design' },
      { path: '/system-design/microservices', title: 'Microservices' }
    ]
  }
};

// System Design All Topics Page Data
export const allSystemDesignTopics = [
  // Core Concepts
  {
    category: 'Core Concepts',
    topics: [
      {
        id: 'scalability',
        title: 'Scalability Fundamentals',
        description: 'Understanding horizontal and vertical scaling strategies',
        difficulty: 'intermediate',
        estimatedTime: '8 min read',
        icon: '📈'
      },
      {
        id: 'load-balancing',
        title: 'Load Balancing',
        description: 'Distribute traffic across multiple servers efficiently',
        difficulty: 'intermediate',
        estimatedTime: '7 min read',
        icon: '⚖️'
      },
      {
        id: 'databases',
        title: 'Database Design',
        description: 'SQL vs NoSQL, sharding, and replication strategies',
        difficulty: 'advanced',
        estimatedTime: '12 min read',
        icon: '🗄️'
      },
      {
        id: 'caching',
        title: 'Caching Strategies',
        description: 'Redis, Memcached, and CDN implementation',
        difficulty: 'intermediate',
        estimatedTime: '9 min read',
        icon: '⚡'
      },
      {
        id: 'security',
        title: 'System Security',
        description: 'Authentication, authorization, and data protection',
        difficulty: 'advanced',
        estimatedTime: '10 min read',
        icon: '🔒'
      },
      {
        id: 'microservices',
        title: 'Microservices Architecture',
        description: 'Service-oriented architecture and communication patterns',
        difficulty: 'advanced',
        estimatedTime: '11 min read',
        icon: '🏗️'
      }
    ]
  },
  // Case Studies
  {
    category: 'Case Studies',
    topics: [
      {
        id: 'whatsapp',
        title: 'Design WhatsApp',
        description: 'Real-time messaging system with billions of users',
        difficulty: 'expert',
        estimatedTime: '15 min read',
        icon: '💬'
      },
      {
        id: 'twitter',
        title: 'Design Twitter',
        description: 'Social media platform with timeline and trending features',
        difficulty: 'advanced',
        estimatedTime: '14 min read',
        icon: '🐦'
      },
      {
        id: 'uber',
        title: 'Design Uber',
        description: 'Ride-sharing application with real-time tracking',
        difficulty: 'expert',
        estimatedTime: '16 min read',
        icon: '🚗'
      }
    ]
  }
];
